import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Tag,
  Space,
  App,
  Popconfirm,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Card,
  Modal,
  Tooltip,
  Badge,
  Alert,
  Descriptions
} from 'antd';
import {
  DeleteOutlined,
  FilePdfOutlined,
  EyeOutlined,
  SearchOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  FileTextOutlined,
  CloudUploadOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  canDeleteInvoice,
  canSendInvoiceToKSeF,
  isKSeFSurfaceVisible,
} from '../modules/invoices/invoiceAccessPolicy';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface Invoice {
  id: string;
  number: string;
  orderId: string;
  orderNumber?: string;
  /** Canonical operator-facing OMS number (manual number when active, raw otherwise). */
  displayOrderNumber?: string;
  customerName?: string;
  type: 'FINAL' | 'PROFORMA' | 'ADVANCE';
  status: 'DRAFT' | 'ISSUED' | 'PAID' | 'CANCELLED';
  issueDate: string;
  dueDate?: string;
  totalAmount: number;
  currency: string;
  pdfUrl?: string;
  ksefNumber?: string;
  ksefStatus?: 'NONE' | 'PENDING' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'ERROR';
  ksefSentAt?: string;
  ksefReferenceNumber?: string;
  createdAt: string;
  updatedAt: string;
}

interface KSeFStatus {
  /** True when the KSeF module + integration are on (gate for the whole surface). */
  moduleEnabled?: boolean;
  /** True when the external KSeF system is reachable/ok. */
  available: boolean;
  mode: string;
  message: string;
}

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['invoices', 'common']);
  const { message } = App.useApp();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingToKSeF, setSendingToKSeF] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [ksefStatus, setKsefStatus] = useState<KSeFStatus | null>(null);
  const [xmlPreviewVisible, setXmlPreviewVisible] = useState(false);
  const [xmlContent, setXmlContent] = useState<string>('');

  const currentUser = authService.getCurrentUser();
  const canDelete = canDeleteInvoice(currentUser);
  const canSendToKSeF = canSendInvoiceToKSeF(currentUser);

  // Master gate for the whole KSeF surface. Driven by status.moduleEnabled: the
  // surface is present when the KSeF module + integration are on. When the
  // module is off we hide every KSeF affordance so the page looks like KSeF does
  // not exist. (isKSeFSurfaceVisible falls back to available===true for a stale
  // backend that predates the moduleEnabled/available split.)
  const ksefSurfaceVisible = isKSeFSurfaceVisible(ksefStatus);

  // Module on but the external KSeF system is temporarily unreachable. The
  // surface stays visible (operators keep seeing their sent-invoice statuses),
  // but we show an offline Tag + warning Alert. moduleEnabled may be absent on a
  // stale backend; in that case the surface is gated on available===true so this
  // is never true, matching the old behavior.
  const ksefOffline = ksefSurfaceVisible && ksefStatus?.available === false;

  // Filter states
  const [filters, setFilters] = useState<{
    status: string[];
    type: string[];
    ksefStatus: string[];
    dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    search: string;
  }>({
    status: [],
    type: [],
    ksefStatus: [],
    dateRange: null,
    search: ''
  });

  useEffect(() => {
    fetchInvoices();
    checkKSeFStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await api.get('/invoices');
      setInvoices(response.data);
    } catch (error: any) {
      message.error(error.response?.data?.error || t('invoices:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const checkKSeFStatus = async () => {
    try {
      const response = await api.get('/invoices/ksef/status');
      setKsefStatus(response.data);
      // The probe message may be an English backend string; keep it out of the
      // Polish-first UI and only surface it for diagnostics.
      if (response.data?.available === false && response.data?.message) {
        console.debug('KSeF status probe:', response.data.message);
      }
    } catch (error) {
      console.error('Failed to check KSeF status:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/invoices/${id}`);
      message.success(t('invoices:success.deleted'));
      fetchInvoices();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('invoices:error.deleteFailed'));
    }
  };

  const handleDownloadPDF = async (id: string, number: string) => {
    setDownloading(id);
    try {
      const response = await api.get(`/invoices/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${number.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(t('invoices:success.pdfDownloaded'));
    } catch (error: any) {
      message.error(error.response?.data?.error || t('invoices:error.pdfFailed'));
    } finally {
      setDownloading(null);
    }
  };

  const handlePreviewXML = async (id: string) => {
    try {
      const response = await api.get(`/invoices/${id}/ksef/xml`);
      setXmlContent(response.data);
      setXmlPreviewVisible(true);
    } catch (error: any) {
      message.error(error.response?.data?.error || t('invoices:error.xmlFailed'));
    }
  };

  const handleSendToKSeF = async (id: string) => {
    setSendingToKSeF(id);
    try {
      await api.post(`/invoices/${id}/ksef/send`);
      message.success(t('invoices:success.sentToKSeF'));
      fetchInvoices();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('invoices:error.sendToKSeFailed'));
    } finally {
      setSendingToKSeF(null);
    }
  };

  const handleValidateKSeF = async (id: string) => {
    try {
      const response = await api.get(`/invoices/${id}/ksef/validate`);
      const validation = response.data;

      if (validation.valid) {
        message.success(t('invoices:validation.valid'));
      } else {
        Modal.warning({
          title: t('invoices:validation.invalid'),
          content: (
            <div>
              {validation.errors.map((err: string, i: number) => (
                <p key={i} style={{ color: 'red' }}>{err}</p>
              ))}
              {validation.warnings.map((warn: string, i: number) => (
                <p key={i} style={{ color: 'orange' }}>{warn}</p>
              ))}
            </div>
          )
        });
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || t('invoices:error.validationFailed'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'default';
      case 'ISSUED': return 'processing';
      case 'PAID': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'FINAL': return 'blue';
      case 'PROFORMA': return 'orange';
      case 'ADVANCE': return 'purple';
      default: return 'default';
    }
  };

  const getKSeFStatusIcon = (status?: string) => {
    switch (status) {
      case 'ACCEPTED': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'SENT': return <SyncOutlined spin style={{ color: '#1890ff' }} />;
      case 'PENDING': return <SyncOutlined spin style={{ color: '#faad14' }} />;
      case 'REJECTED':
      case 'ERROR': return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default: return null;
    }
  };

  const getKSeFStatusColor = (status?: string) => {
    switch (status) {
      case 'ACCEPTED': return 'success';
      case 'SENT': return 'processing';
      case 'PENDING': return 'warning';
      case 'REJECTED':
      case 'ERROR': return 'error';
      default: return 'default';
    }
  };

  // Filter documents
  const filteredInvoices = useMemo(() => {
    let filtered = [...invoices];

    if (filters.status.length > 0) {
      filtered = filtered.filter(inv => filters.status.includes(inv.status));
    }

    if (filters.type.length > 0) {
      filtered = filtered.filter(inv => filters.type.includes(inv.type));
    }

    if (filters.ksefStatus.length > 0) {
      filtered = filtered.filter(inv =>
        filters.ksefStatus.includes(inv.ksefStatus || 'NONE')
      );
    }

    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = filters.dateRange[0].startOf('day');
      const endDate = filters.dateRange[1].endOf('day');
      filtered = filtered.filter(inv => {
        const issueDate = dayjs(inv.issueDate);
        return issueDate.isAfter(startDate) && issueDate.isBefore(endDate);
      });
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.number.toLowerCase().includes(searchLower) ||
        inv.customerName?.toLowerCase().includes(searchLower) ||
        inv.orderNumber?.toLowerCase().includes(searchLower) ||
        inv.displayOrderNumber?.toLowerCase().includes(searchLower) ||
        inv.ksefNumber?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [invoices, filters]);

  const clearFilters = () => {
    setFilters({
      status: [],
      type: [],
      ksefStatus: [],
      dateRange: null,
      search: ''
    });
  };

  const columns = [
    {
      title: t('invoices:fields.number'),
      dataIndex: 'number',
      key: 'number',
      sorter: (a: Invoice, b: Invoice) => a.number.localeCompare(b.number),
      render: (number: string, record: Invoice) => (
        <Space>
          <strong>{number}</strong>
          {ksefSurfaceVisible && record.ksefNumber && (
            <Tooltip title={`KSeF: ${record.ksefNumber}`}>
              <Badge status="success" />
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: t('invoices:fields.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>
          {t(`invoices:types.${type.toLowerCase()}`)}
        </Tag>
      )
    },
    {
      title: t('invoices:fields.issueDate'),
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (date: string) => formatDate(date),
      sorter: (a: Invoice, b: Invoice) => dayjs(a.issueDate).unix() - dayjs(b.issueDate).unix()
    },
    {
      title: t('invoices:fields.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name: string) => name || '-'
    },
    {
      title: t('invoices:fields.orderNumber'),
      key: 'orderNumber',
      render: (record: Invoice) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/orders/${record.orderId}`)}
        >
          {record.displayOrderNumber || record.orderNumber || '-'}
        </Button>
      )
    },
    {
      title: t('invoices:fields.amount'),
      key: 'amount',
      render: (record: Invoice) => formatCurrency(record.totalAmount, record.currency),
      sorter: (a: Invoice, b: Invoice) => a.totalAmount - b.totalAmount
    },
    {
      title: t('invoices:fields.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => date ? formatDate(date) : '-'
    },
    {
      title: t('invoices:fields.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {t(`invoices:status.${status.toLowerCase()}`)}
        </Tag>
      )
    },
    // KSeF status column is only present when the KSeF module is available.
    ...(ksefSurfaceVisible ? [{
      title: 'KSeF',
      key: 'ksef',
      render: (record: Invoice) => {
        if (record.type === 'PROFORMA') {
          return <Tag>{t('invoices:ksef.notApplicable')}</Tag>;
        }

        const status = record.ksefStatus || 'NONE';
        return (
          <Space>
            {getKSeFStatusIcon(status)}
            <Tag color={getKSeFStatusColor(status)}>
              {t(`invoices:ksef.status.${status.toLowerCase()}`)}
            </Tag>
          </Space>
        );
      }
    }] : []),
    {
      title: t('common:actions'),
      key: 'actions',
      width: 280,
      render: (record: Invoice) => (
        <Space wrap>
          <Tooltip title={t('common:view')}>
            <Button
              icon={<EyeOutlined />}
              onClick={() => {
                setPreviewInvoice(record);
                setPreviewVisible(true);
              }}
              size="small"
            />
          </Tooltip>

          <Tooltip title="PDF">
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => handleDownloadPDF(record.id, record.number)}
              loading={downloading === record.id}
              size="small"
            />
          </Tooltip>

          {ksefSurfaceVisible && record.type !== 'PROFORMA' && (
            <>
              <Tooltip title={t('invoices:actions.previewXml')}>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => handlePreviewXML(record.id)}
                  size="small"
                />
              </Tooltip>

              <Tooltip title={t('invoices:actions.validateKSeF')}>
                <Button
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => handleValidateKSeF(record.id)}
                  size="small"
                />
              </Tooltip>

              {canSendToKSeF && record.status === 'ISSUED' && (!record.ksefStatus || record.ksefStatus === 'NONE' || record.ksefStatus === 'ERROR') && (
                <Tooltip title={t('invoices:actions.sendToKSeF')}>
                  <Button
                    type="primary"
                    icon={<CloudUploadOutlined />}
                    onClick={() => handleSendToKSeF(record.id)}
                    loading={sendingToKSeF === record.id}
                    size="small"
                  />
                </Tooltip>
              )}
            </>
          )}

          {canDelete && record.status === 'DRAFT' && (
            <Popconfirm
              title={t('invoices:confirm.delete')}
              onConfirm={() => { void handleDelete(record.id); }}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('invoices:title')}</h1>
        <Space>
          {ksefSurfaceVisible && (
            <Tag color={ksefOffline ? 'error' : 'success'}>
              KSeF: {ksefOffline ? t('invoices:ksef.offline') : t('invoices:ksef.online')}
            </Tag>
          )}
        </Space>
      </div>

      {/* Module on but the KSeF servers are unreachable: keep the surface but warn.
          Operator-facing copy stays the i18n string; the raw backend probe
          message (often English) goes to the console for diagnostics only, so it
          never leaks onto the Polish-first screen. */}
      {ksefOffline && (
        <Alert
          message={t('invoices:ksef.systemUnavailable')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder={t('invoices:filters.search')}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              mode="multiple"
              placeholder={t('invoices:filters.type')}
              value={filters.type}
              onChange={(value) => setFilters({ ...filters, type: value })}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="FINAL">{t('invoices:types.final')}</Option>
              <Option value="PROFORMA">{t('invoices:types.proforma')}</Option>
              <Option value="ADVANCE">{t('invoices:types.advance')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              mode="multiple"
              placeholder={t('invoices:filters.status')}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="DRAFT">{t('invoices:status.draft')}</Option>
              <Option value="ISSUED">{t('invoices:status.issued')}</Option>
              <Option value="PAID">{t('invoices:status.paid')}</Option>
              <Option value="CANCELLED">{t('invoices:status.cancelled')}</Option>
            </Select>
          </Col>
          {ksefSurfaceVisible && (
            <Col xs={24} sm={12} md={4}>
              <Select
                mode="multiple"
                placeholder={t('invoices:filters.ksefStatus')}
                value={filters.ksefStatus}
                onChange={(value) => setFilters({ ...filters, ksefStatus: value })}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value="NONE">{t('invoices:ksef.status.none')}</Option>
                <Option value="PENDING">{t('invoices:ksef.status.pending')}</Option>
                <Option value="SENT">{t('invoices:ksef.status.sent')}</Option>
                <Option value="ACCEPTED">{t('invoices:ksef.status.accepted')}</Option>
                <Option value="ERROR">{t('invoices:ksef.status.error')}</Option>
              </Select>
            </Col>
          )}
          <Col xs={24} sm={12} md={4}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col xs={24} sm={12} md={2}>
            <Button
              icon={<ClearOutlined />}
              onClick={clearFilters}
              style={{ width: '100%' }}
            >
              {t('common:clear')}
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={filteredInvoices}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => t('common:totalItems', { count: total })
        }}
        scroll={{ x: 1200 }}
      />

      {/* Invoice Preview Modal */}
      <Modal
        title={t('invoices:preview.title')}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            {t('common:close')}
          </Button>,
          <Button
            key="pdf"
            type="primary"
            icon={<FilePdfOutlined />}
            onClick={() => previewInvoice && handleDownloadPDF(previewInvoice.id, previewInvoice.number)}
            loading={downloading === previewInvoice?.id}
          >
            {t('invoices:actions.downloadPdf')}
          </Button>
        ]}
        width={700}
      >
        {previewInvoice && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('invoices:fields.number')} span={2}>
              <strong>{previewInvoice.number}</strong>
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.type')}>
              <Tag color={getTypeColor(previewInvoice.type)}>
                {t(`invoices:types.${previewInvoice.type.toLowerCase()}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.status')}>
              <Tag color={getStatusColor(previewInvoice.status)}>
                {t(`invoices:status.${previewInvoice.status.toLowerCase()}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.issueDate')}>
              {formatDate(previewInvoice.issueDate)}
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.dueDate')}>
              {previewInvoice.dueDate ? formatDate(previewInvoice.dueDate) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.customer')} span={2}>
              {previewInvoice.customerName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.orderNumber')}>
              {previewInvoice.displayOrderNumber || previewInvoice.orderNumber || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.amount')}>
              <strong>{formatCurrency(previewInvoice.totalAmount, previewInvoice.currency)}</strong>
            </Descriptions.Item>

            {ksefSurfaceVisible && previewInvoice.type !== 'PROFORMA' && (
              <>
                <Descriptions.Item label={t('invoices:ksef.status.label')} span={2}>
                  <Space>
                    {getKSeFStatusIcon(previewInvoice.ksefStatus)}
                    <Tag color={getKSeFStatusColor(previewInvoice.ksefStatus)}>
                      {t(`invoices:ksef.status.${(previewInvoice.ksefStatus || 'none').toLowerCase()}`)}
                    </Tag>
                  </Space>
                </Descriptions.Item>
                {previewInvoice.ksefNumber && (
                  <Descriptions.Item label={t('invoices:ksef.number')} span={2}>
                    {previewInvoice.ksefNumber}
                  </Descriptions.Item>
                )}
                {previewInvoice.ksefSentAt && (
                  <Descriptions.Item label={t('invoices:ksef.sentAt')} span={2}>
                    {formatDate(previewInvoice.ksefSentAt)}
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* XML Preview Modal */}
      <Modal
        title={t('invoices:preview.xmlTitle')}
        open={xmlPreviewVisible}
        onCancel={() => setXmlPreviewVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="close" onClick={() => setXmlPreviewVisible(false)}>
            {t('common:close')}
          </Button>
        ]}
        width={800}
      >
        <pre style={{
          backgroundColor: '#f5f5f5',
          padding: '16px',
          borderRadius: '4px',
          maxHeight: '500px',
          overflow: 'auto',
          fontSize: '12px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all'
        }}>
          {xmlContent}
        </pre>
      </Modal>
    </div>
  );
};

export default Invoices;
